import React, { useEffect, useState } from "react";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { GetLanguageString } from "../../helper/Components";
import {
  customOption,
  customSingleValue,
  applyClassToCharacters,
  LoadingSpinner,
  CheckPermission,
} from "../../../constants/const";
import { IoArrowBackSharp } from "react-icons/io5";
import { TbTrash, TbTrashOff } from "react-icons/tb";
import { MdOutlineAddCircleOutline } from "react-icons/md";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { GetCustomStyles } from "../../helper/Method";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import swal from "sweetalert";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import { encryptStorage } from "../../../constants/EncryptStorage";
import AddNewBankAccount from "../transfers/amanatToCashInHand/AddNewBankAccount";
import { addMember } from "../../member/ApiMember";
import { FaPlus } from "react-icons/fa";

// ---------------- Reusable Components ----------------
const CustomSelect = ({
  label,
  name,
  options,
  value,
  onChange,
  loading,
  error,
  placeholder,
  isDisabled,
}) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Select
      components={{ Option: customOption, SingleValue: customSingleValue }}
      options={options}
      name={name}
      value={value}
      className="basic-select"
      classNamePrefix="select"
      isLoading={loading}
      isDisabled={isDisabled || options.length === 0}
      onChange={onChange}
      placeholder={placeholder}
      {...(error && { invalid: true })}
    />
    {error && <h4 className="text-error">{error}</h4>}
  </FormGroup>
);

const CustomInput = ({ label, name, value, onChange, error, ...rest }) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Input
      name={name}
      type="text"
      value={value}
      onChange={onChange}
      className="no-drag"
      {...rest}
    />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

// ---------------- Main Component ----------------
const ExpenseFromAmanat = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const unitTypeName = encryptStorage.getItem("unitTypeName");

  // ---------- State ----------
  const [loading, setLoading] = useState({
    source: false,
    accounts: false,
    dimensions: false,
    submit: false,
  });

  const [financeCategories, setFinanceCategories] = useState([]);
  const [financeAccounts, setFinanceAccounts] = useState([]);
  const [financeDimensions, setFinanceDimensions] = useState([]);
  const [unitBankAccounts, setUnitBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [refreshBankList, setRefreshBankList] = useState(false);
  const [handleAddNew, setHandleAddNew] = useState(false);

  const [formData, setFormData] = useState({
    selectedFinanceCategory: null,
    selectedAccount: null,
    selectedDimension: null,
    selectedBankAccount: null,
    date: new Date(),
    amount: "",
    memo: "",
    quantity: "",
    price: "",
    serialNumber: "",
    entries: [
      {
        financeAccount: null,
        dimension2: null,
        amount: "",
        quantity: "",
        price: "",
        lineMemo: "",
      },
    ],
  });

  const [errors, setErrors] = useState({});

  // ---------- Handlers ----------
  const handleChange = (field, value) => {
    if (field === "selectedFinanceCategory") {
      setFormData((prev) => ({
        ...prev,
        selectedFinanceCategory: value,
        entries: [
          {
            financeAccount: null,
            dimension2: null,
            amount: "",
            quantity: "",
            price: "",
            lineMemo: "",
          },
        ],
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    setErrors((prev) => ({ ...prev, [field]: "", entries: "" }));
  };

  const getTotalAmount = (entries) => {
    const total = entries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );
    return Number.isInteger(total) ? total.toString() : total.toFixed(2);
  };

  const handleEntryChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedEntries = prev.entries.map((entry, idx) =>
        idx === index ? { ...entry, [field]: value } : entry,
      );
      return { ...prev, entries: updatedEntries };
    });
    setErrors((prev) => ({ ...prev, entries: "" }));
  };

  const addEntry = () => {
    setFormData((prev) => ({
      ...prev,
      entries: [
        ...prev.entries,
        {
          financeAccount: financeAccounts[0] || null,
          dimension2: financeDimensions[0] || null,
          amount: "",
          quantity: "",
          price: "",
          lineMemo: "",
        },
      ],
    }));
  };

  const removeEntry = (index) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((_, idx) => idx !== index),
    }));
  };

  // Helper function to pad numbers to 4 digits with leading zeros
  const padTo4Digits = (value) => {
    if (!value) return "0000";
    return String(value).padStart(4, "0");
  };

  const handleBankAccountChange = (option) => {
    setSelectedBankAccount(option);
    setFormData((prev) => ({ ...prev, selectedBankAccount: option }));
  };

  const showQuantityPrice =
    formData.selectedFinanceCategory?.label1 === "asset" ||
    formData.selectedFinanceCategory?.label1 === "maktaba" ||
    formData.selectedFinanceCategory?.label1 === "misc";

  const isMaktabaOrLoanMarkaz =
    formData.selectedFinanceCategory?.label1.includes("markaz");

  const isLoanIndividual =
    formData.selectedFinanceCategory?.label1 === "return_loan_individual";

  const useEntryDetails = !isMaktabaOrLoanMarkaz && !isLoanIndividual;

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const isValid = validate();
    if (!isValid) {
      return;
    }
    const totalAmount = useEntryDetails
      ? getTotalAmount(formData.entries)
      : formData.amount;

    const entriesPayload = useEntryDetails
      ? formData.entries.map((entry) => {
          const fallbackMemo = entry.lineMemo || formData.memo;
          return {
            financeAccount: entry.financeAccount ?? null,
            dimension2: entry.dimension2 ?? null,
            amount: entry.amount,
            lineMemo: showQuantityPrice
              ? `${padTo4Digits(entry.quantity)} x ${padTo4Digits(entry.price)}${fallbackMemo ? ` (${fallbackMemo})` : ""}`
              : fallbackMemo,
          };
        })
      : [
          {
            financeAccount: formData.selectedAccount ?? null,
            dimension2: formData.selectedDimension ?? null,
            amount: formData.amount,
            lineMemo: showQuantityPrice
              ? `${padTo4Digits(formData.quantity)} x ${padTo4Digits(formData.price)}${formData.memo ? ` (${formData.memo})` : ""}`
              : formData.memo,
          },
        ];

    const payload = {
      financeAccount: entriesPayload[0]?.financeAccount ?? null,
      dimension2: entriesPayload[0]?.dimension2 ?? null,
      rafiqBankId: formData.selectedBankAccount?.value ?? null,
      category: formData.selectedFinanceCategory ?? null,
      date: formData.date,
      amount: totalAmount,
      serialNumber: formData.serialNumber,
      memo: useEntryDetails ? formData.memo : entriesPayload[0]?.lineMemo,
      unitId: loggedInUnitId,
      entries: entriesPayload,
    };

    setLoading((prev) => ({ ...prev, submit: true }));
    ApiFinanceLocal()
      .AddExpenseFromAmanat(payload)
      .then((res) => {
        const data = res.data;

        if (data?.statusCode === "200") {
          swal({
            title: "Success",
            text: t(data.message) || t("funds_expense_added_successfully"),
            icon: "success",
            buttons: "OK",
          }).then(() => {
            history.goBack();
          });
          return;
        }

        swal({
          title: "Error",
          text: data?.message || "Failed to add Expense",
          icon: "error",
          buttons: "OK",
        });

        setLoading((prev) => ({ ...prev, submit: false }));
      })
      .catch(() => {
        // 🚨 ONLY network / server crash comes here
        swal({
          title: "Error",
          text: "Unable to connect to server. Please try again.",
          icon: "error",
          buttons: "OK",
        });
        setLoading((prev) => ({ ...prev, submit: false }));
      });
  };

  // Fetch finance categories on mount
  useEffect(() => {
    setLoading((prev) => ({ ...prev, source: true }));
    ApiFinance()
      .GetFinanceAccountCategoriesListByType("expense")
      .then((res) => {
        setFinanceCategories(res.data || []);

        setFormData((prev) => ({
          ...prev,
          selectedFinanceCategory: res.data[0] || null,
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: err,
          text: err.response?.data,
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, source: false })));
  }, [unitTypeName]);

  // Fetch unit bank accounts
  useEffect(() => {
    if (!loggedInUnitId) return;
    addMember()
      .GetUnitBankAccounts(loggedInUnitId)
      .then((res) => {
        const list = res.data || [];
        setUnitBankAccounts(list);
        if (list.length > 0 && !refreshBankList) {
          setSelectedBankAccount(list[0]);
          setFormData((prev) => ({ ...prev, selectedBankAccount: list[0] }));
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setRefreshBankList(false));
  }, [loggedInUnitId, refreshBankList]);

  // Fetch accounts & dimensions when category changes
  useEffect(() => {
    const categoryId = formData.selectedFinanceCategory?.value;
    if (!categoryId) return;

    setLoading((prev) => ({ ...prev, accounts: true, dimensions: true }));
    setFinanceAccounts([]);
    setFinanceDimensions([]);
    setFormData((prev) => ({
      ...prev,
      selectedAccount: null,
      selectedDimension: null,
      entries: [
        {
          financeAccount: null,
          dimension2: null,
          amount: "",
          quantity: "",
          price: "",
          lineMemo: "",
        },
      ],
    }));

    // Accounts
    ApiFinance()
      .GetFinanceCategoryAccountsById(categoryId)
      .then((res) => {
        setFinanceAccounts(res.data || []);
        setFormData((prev) => ({
          ...prev,
          selectedAccount: res.data[0] || null,
          entries: prev.entries.map((entry) => ({
            ...entry,
            financeAccount: res.data[0] || null,
          })),
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: err,
          text: err.response?.data,
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, accounts: false })));

    // Dimensions
    ApiFinance()
      .GetFinanceCategoryDimensionsById(categoryId)
      .then((res) => {
        setFinanceDimensions(res.data || []);
        setFormData((prev) => ({
          ...prev,
          selectedDimension: res.data[0] || null,
          entries: prev.entries.map((entry) => ({
            ...entry,
            dimension2: res.data[0] || null,
          })),
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: err,
          text: err.response?.data,
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, dimensions: false })));
  }, [formData.selectedFinanceCategory]);

  // ---------- Validation ----------
  const validate = () => {
    const temp = {};
    const totalAmount = useEntryDetails
      ? getTotalAmount(formData.entries)
      : formData.amount;
    const hasInvalidEntry = formData.entries.some((entry) => {
      if (!entry.financeAccount || !entry.dimension2 || !entry.amount || Number(entry.amount) <= 0) {
        return true;
      }
      if (showQuantityPrice && (!entry.quantity || !entry.price)) {
        return true;
      }
      return false;
    });

    temp.entries =
      useEntryDetails &&
      (formData.entries.length === 0 || hasInvalidEntry) ? (
        <GetLanguageString props="funds_add_fund_amount_paid_error" />
      ) : (
        ""
      );

    temp.amount = totalAmount ? (
      ""
    ) : (
      <GetLanguageString props="funds_add_fund_amount_paid_error" />
    );

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  // ---------- Render ----------
  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox">
        <div className="ibox-title d-flex justify-content-between align-items-center">
          <h5>
            {<GetLanguageString props="finance_expense_amanat_account" />}
          </h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>

        <div className="ibox-content">
          {loading.source || loading.submit ? (
            LoadingSpinner()
          ) : (
            <Form onSubmit={handleSubmit} noValidate>
              <Row>
                {handleAddNew && (
                  <AddNewBankAccount
                    setModal={setHandleAddNew}
                    getAddedBankAccount={setSelectedBankAccount}
                    setRefreshBankList={setRefreshBankList}
                  />
                )}
                <Col md="3">
                  <CustomSelect
                    name="selectedFinanceCategory"
                    label="funds_source"
                    options={financeCategories}
                    value={formData.selectedFinanceCategory}
                    onChange={(val) =>
                      handleChange("selectedFinanceCategory", val)
                    }
                    error={errors.selectedFinanceCategory}
                    placeholder={t("member_select")}
                  />
                </Col>
                {isMaktabaOrLoanMarkaz ? null : isLoanIndividual ? (
                  <Col md="3">
                    <CustomInput
                      name="serialNumber"
                      label="notifications_see_changes_serial_number"
                      value={formData.serialNumber}
                      onChange={(e) =>
                        handleChange("serialNumber", e.target.value)
                      }
                      error={errors.serialNumber}
                    />
                  </Col>
                ) : null}
                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_date" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      maxDate={new Date()}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <CustomInput
                    name="memo"
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>
                {!useEntryDetails && (
                  <Col md="3">
                    <CustomInput
                      name="amount"
                      label="funds_add_fund_amount"
                      value={formData.amount}
                      onChange={(e) => handleChange("amount", e.target.value)}
                      error={errors.amount}
                    />
                  </Col>
                )}
                {!useEntryDetails && showQuantityPrice && (
                  <Col md="3" className="d-flex">
                    <div style={{ flex: "0 0 50%", paddingRight: "8px" }}>
                      <CustomInput
                        name="quantity"
                        label="finance_other_income_quantity"
                        value={formData.quantity}
                        onChange={(e) =>
                          handleChange("quantity", e.target.value)
                        }
                        error={errors.quantity}
                      />
                    </div>

                    <div style={{ flex: "0 0 50%", paddingLeft: "8px" }}>
                      <CustomInput
                        name="price"
                        label="finance_other_income_price"
                        value={formData.price}
                        onChange={(e) => handleChange("price", e.target.value)}
                        error={errors.price}
                      />
                    </div>
                  </Col>
                )}
                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="fund_bank_account" />}
                    </Label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <div style={{ flex: 1 }}>
                        <Select
                          components={{ Option: customOption }}
                          options={unitBankAccounts}
                          value={
                            selectedBankAccount
                              ? {
                                  value: selectedBankAccount.value,
                                  label: applyClassToCharacters(
                                    selectedBankAccount.label,
                                    "ur",
                                  ),
                                }
                              : null
                          }
                          onChange={handleBankAccountChange}
                          placeholder={t("member_select")}
                          classNamePrefix="select"
                        />
                      </div>

                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => setHandleAddNew(true)}
                        style={{
                          height: "38px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "0 12px",
                        }}
                      >
                        <FaPlus
                          color="#f7f7f7ff"
                          size={11}
                          style={{ marginRight: 5, marginBottom: 1 }}
                        />
                        {t("tutorial_add_category")}
                      </Button>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
              {useEntryDetails && (
                <Row className="mb-3">
                  <Col md="3">
                    <CustomInput
                      name="totalAmount"
                      label="finance_other_income_total_amount"
                      value={getTotalAmount(formData.entries)}
                      readOnly
                      disabled
                      error={errors.amount}
                    />
                  </Col>
                </Row>
              )}
              {useEntryDetails && (
                <div className="ibox">
                  <div className="ibox-title d-flex justify-content-between align-items-center">
                    <h5>
                      {
                        <GetLanguageString props="finance_other_income_entry_details" />
                      }
                    </h5>
                    <MdOutlineAddCircleOutline
                      size={25}
                      color="#3ba147"
                      onClick={addEntry}
                      style={{ marginRight: 6, marginBottom: 1 }}
                    />
                  </div>
                  <div className="ibox-content">
                    {formData.entries.map((entry, index) => (
                      <Row key={index} className="mb-3 align-items-end">
                        <Col md="3">
                          <CustomSelect
                            name={`financeAccount-${index}`}
                            label="funds_expense_head"
                            options={financeAccounts}
                            value={entry.financeAccount}
                            onChange={(val) =>
                              handleEntryChange(index, "financeAccount", val)
                            }
                            error={errors.entries}
                            loading={loading.accounts}
                            placeholder={
                              loading.accounts
                                ? t("finance_expense_heads_loading")
                                : financeAccounts.length > 0
                                  ? t("finance_expense_heads_select")
                                  : t("finance_expense_heads_empty")
                            }
                          />
                        </Col>
                        <Col md="3">
                          <CustomSelect
                            name={`dimension2-${index}`}
                            label="funds_expense_sub_head"
                            options={financeDimensions}
                            value={entry.dimension2}
                            onChange={(val) =>
                              handleEntryChange(index, "dimension2", val)
                            }
                            error={errors.entries}
                            loading={loading.dimensions}
                            placeholder={
                              loading.dimensions
                                ? t("finance_expense_sub_heads_loading")
                                : financeDimensions.length > 0
                                  ? t("finance_expense_sub_heads_select")
                                  : t("finance_expense_sub_heads_empty")
                            }
                          />
                        </Col>
                        <Col md={showQuantityPrice ? "1" : "2"}>
                          <CustomInput
                            name={`entryAmount-${index}`}
                            label="funds_add_fund_amount"
                            value={entry.amount}
                            onChange={(e) =>
                              handleEntryChange(index, "amount", e.target.value)
                            }
                            error={errors.entries}
                          />
                        </Col>
                        {showQuantityPrice && (
                          <>
                            <Col md="1">
                              <CustomInput
                                name={`quantity-${index}`}
                                label="finance_other_income_quantity"
                                value={entry.quantity}
                                onChange={(e) =>
                                  handleEntryChange(
                                    index,
                                    "quantity",
                                    e.target.value,
                                  )
                                }
                                error={errors.entries}
                              />
                            </Col>
                            <Col md="1">
                              <CustomInput
                                name={`price-${index}`}
                                label="finance_other_income_price"
                                value={entry.price}
                                onChange={(e) =>
                                  handleEntryChange(
                                    index,
                                    "price",
                                    e.target.value,
                                  )
                                }
                                error={errors.entries}
                              />
                            </Col>
                          </>
                        )}
                        <Col md="2">
                          <CustomInput
                            name={`lineMemo-${index}`}
                            label="transactions_memo"
                            value={entry.lineMemo}
                            onChange={(e) =>
                              handleEntryChange(
                                index,
                                "lineMemo",
                                e.target.value,
                              )
                            }
                            error={errors.entries}
                          />
                        </Col>
                        <Col
                          md="auto"
                          style={{ position: "relative", bottom: "23px" }}
                          className="text-center"
                        >
                          {formData.entries.length === 1 ? (
                            <TbTrashOff
                              size={25}
                              color="#a72424"
                              style={{ opacity: 0.5 }}
                            />
                          ) : (
                            <TbTrash
                              size={25}
                              color="#bb0e0e"
                              style={{ cursor: "pointer" }}
                              onClick={() => removeEntry(index)}
                            />
                          )}
                        </Col>
                      </Row>
                    ))}
                  </div>
                </div>
              )}
              <Row>
                <Col md="9"></Col>
                <Col md="3" className="text-end">
                  {CheckPermission("CanAddFunds") ? (
                    <>
                      <Button color="white" onClick={() => history.goBack()}>
                        {<GetLanguageString props="common_cancel" />}
                      </Button>
                      &nbsp;&nbsp;
                      <Button
                        disabled={Object.values(loading).some(
                          (val) => val === true,
                        )}
                        color="primary"
                        onClick={() => handleSubmit(false)}
                      >
                        {<GetLanguageString props="funds_add_expense" />}
                      </Button>
                    </>
                  ) : null}
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(ExpenseFromAmanat);
